import type { IVideoEditor } from '@src/editor/interface/IVideoEditor';

/**
 * PLAT-11689 — whether the video's captions are on screen, shared by every
 * surface that shows or toggles it (the Transcript header's Cap button in the
 * clip preview and Creator Studio, the caption-language switch, a produce
 * from no track in `commandProduceKaraoke`), so they all agree.
 *
 * "Hidden" is `KaraokeConfig.hidden`: the lines stay produced and
 * `KaraokeClip.toJSON` emits them `visible: false`, so Show brings back the
 * exact same lines without a re-produce. It lives on the config so it survives
 * regeneration and is saved with the clip's caption data.
 */
export type CaptionsVisibilityEditor = Pick<
  IVideoEditor,
  'getDefaultKaraokeConfig' | 'getKaraokeConfigs' | 'commandUpdateKaraokeConfig'
>;

/**
 * The one rule for rebuilding a caption config from a template (seed,
 * language variant, template apply): the video's captions stay exactly as
 * visible as they were. The template's own `hidden`, if it somehow carries
 * one, never leaks in — only `previous` decides.
 */
export function keepCaptionsVisibility<T extends object>(
  previous: { hidden?: boolean } | null | undefined,
  next: T,
): T {
  const { hidden: _fromTemplate, ...rest } = next as T & { hidden?: boolean };
  return (previous?.hidden === true ? { ...rest, hidden: true } : rest) as T;
}

export function areCaptionsHidden(editor: CaptionsVisibilityEditor): boolean {
  return editor.getDefaultKaraokeConfig()?.hidden === true;
}

/**
 * Hide or show the captions on every caption config. With
 * `createUndoRedoEntry` (the default — a user's own toggle) the FIRST update
 * carries the undo entry: the hosts snapshot before an undoable command, so
 * one snapshot covers the whole change and the rest ride along silently.
 * Returns whether anything changed.
 */
export function setCaptionsHidden(
  editor: CaptionsVisibilityEditor,
  hidden: boolean,
  { createUndoRedoEntry = true }: { createUndoRedoEntry?: boolean } = {},
): boolean {
  const defaultConfig = editor.getDefaultKaraokeConfig();
  const configs = [
    ...(defaultConfig ? [defaultConfig] : []),
    ...[...editor.getKaraokeConfigs().values()].filter(
      c => c.id !== defaultConfig?.id,
    ),
  ].filter(c => (c.hidden === true) !== hidden);
  let undoEntryPending = createUndoRedoEntry;
  for (const config of configs) {
    const { hidden: _previous, ...rest } = config;
    editor.commandUpdateKaraokeConfig(
      hidden ? { ...rest, hidden: true } : rest,
      undoEntryPending,
    );
    undoEntryPending = false;
  }
  return configs.length > 0;
}
